import { useState } from 'react';
import {
  fetchActivities,
  persistActivities as persistActivitiesApi,
  WholeCollectionWriteClosed,
} from '../lib/api/activities';

// ─────────────────────────────────────────────────────────────────────
// useActivities — the "Aktiviteter" domain (tournaments awaiting signup),
// lifted out of the Padel24AppMain god component (see
// docs/history/DECOMPOSITION_PLAN.md §4/§5, R1..R6). Owns ONE persisted slice:
//   • activities  — Storage key 'activities_v1', TENANT-SCOPED.
//
// The slice has NO §13.20 blanket-persist effect and NO loaded-flag race:
// every write goes through the single self-contained mutator persistActivities.
//
// WHAT MOVED HERE (verbatim):
//   • activities state + setActivities.
//   • persistActivities(next) — the lone SELF-CONTAINED writer: it touches
//     ONLY this slice + Storage. Copied verbatim, INCLUDING its deliberate
//     ordering and error handling — it is OPTIMISTIC (setActivities BEFORE
//     Storage.set, not Storage-first) and SWALLOWS the Storage error (no throw,
//     no return value). Do NOT "fix" either: the many call-sites
//     (recurring generation, reserve, series-edit, cancel, delete, KM-snapshot,
//     …) rely on this fire-and-update behaviour.
//
// WHAT STAYS IN AppShell (uses the exposed setters below as a loader bridge):
//   • The INITIAL load. As with useArchive/usePlayers/useBookingSettings, the
//     atomic mount loader reads 'activities_v1' alongside
//     ~13 other domains in one pass and flushes via setActivities(...).
//     Keeping that loader atomic is a deliberate constraint (R1) — we expose
//     setActivities so it keeps working unchanged.
//   • The two BUSINESS effects, which are cross-domain (read bookings /
//     bookingsRef and call the booking engine mutators):
//       – rolling top-up (topUpRollingSeries) → addManyBookings
//       – auto-cancel (checkAutoCancel)        → deleteManyBookings
//     They consume the exposed persistActivities/activities; they MUST STAY.
//   • Every cross-domain / engine-coupled writer that builds an activities list
//     and then calls persistActivities (handleReserveTournament,
//     buildActivityOnCreate, applySeriesEdit, the preview-activity-
//     commit effect, and the launch/KM/seriespill flows that read
//     `tournament` / call the inline Padel24Engine). They all keep working via
//     the exposed persistActivities — they were never self-contained.
//
// src/ imports ONLY from other src/ modules (Storage from '../lib/storage');
// it never imports from legacy/.
// ─────────────────────────────────────────────────────────────────────

export function useActivities() {
  // ACTIVITIES: tournaments awaiting signup (the Aktiviteter module).
  // Lifted to app level so the home page and the module stay in sync. Tenant-scoped in activities_v1.
  const [activities, setActivities] = useState<any[]>([]);

  // ACTIVITIES: write the activities list (tenant-scoped) + update state.
  // Optimistic ordering (setState-first) + swallowed Storage error — verbatim
  // from the monolith; do NOT make it Storage-first or make it throw/return.
  const persistActivities = async (next: any[]) => {
    setActivities(next);
    try {
      await persistActivitiesApi(next);
    } catch (e) {
      // The optimistic swallow is deliberate and stays — for the BLOB path.
      // The closed door is not a write failure to shrug off: it means a caller
      // is still rewriting the whole list on the http backend, and swallowing
      // it would hide exactly the bug the door exists to expose (#245).
      if (e instanceof WholeCollectionWriteClosed) throw e;
      /* ignore */
    }
  };

  /**
   * Adopt an activity the SERVER has already changed (#245).
   *
   * Not a write: the command committed it, so persisting it back would be the
   * client restating the server's answer — and doing that through the
   * whole-collection PUT is exactly the lost update the commands exist to
   * remove. This only brings the local copy into line.
   */
  const applyServerActivity = (updated: any) => {
    if (!updated?.id) return;
    setActivities((prev: any[]) =>
      (prev || []).map((a: any) => (String(a.id) === String(updated.id) ? updated : a)),
    );
  };

  /**
   * Adopt an activity a command returned, whether or not we already had it.
   *
   * `applyServerActivity` only ever replaces — it is the member flow, where the
   * activity is on screen by definition. A staff save can CREATE one, so this
   * appends when the id is new. Neither writes: the command already committed.
   */
  const adoptServerActivity = (saved: any) => {
    if (!saved?.id) return;
    setActivities((prev: any[]) => {
      const list = prev || [];
      return list.some((a: any) => String(a.id) === String(saved.id))
        ? list.map((a: any) => (String(a.id) === String(saved.id) ? saved : a))
        : [...list, saved];
    });
  };

  /** The same, for a command that returned several. */
  const adoptServerActivities = (saved: any[]) => {
    for (const a of saved || []) adoptServerActivity(a);
  };

  /**
   * Re-read the list from the server.
   *
   * For commands that change MANY rows and answer with a count rather than the
   * documents — the scoped series edit, auto-cancel. Restating them locally
   * would mean reimplementing the server's decision in the browser, which is
   * the drift these commands exist to end.
   */
  const refreshActivities = async () => {
    setActivities(await fetchActivities());
  };

  return {
    activities,
    applyServerActivity,
    adoptServerActivity,
    adoptServerActivities,
    refreshActivities,
    // Exposed for the atomic mount loader (initial flush) and for the
    // cross-domain / engine-coupled writers + business effects that now live
    // in AppShell (rolling top-up, auto-cancel, reserve, series-edit, …).
    setActivities,
    persistActivities,
  };
}
